import React, { useCallback, useEffect, useState } from 'react';
import {
  Row,
  Col,
  Text,
  Button,
  Divider,
  Select,
  Flex,
  Box,
} from '@keyslabor/shared/ui';
import PageLayout from '@/components/PageLayout';
import PageContainer from '@keyslabor/shared/ui/layout/PageContainer';
import CalendarNavigationCompact from '@/components/Calendar/CalendarNavigationCompact';
import { BoxStyledProps } from '@keyslabor/shared/ui/layout/Box/Box.types';
import { useTheme } from 'styled-components';
import useAjax from '@/hooks/useAjax';
import { ajaxRoutes } from '@/globalConstants';
import { TurnOverMeta } from './types';
import { format, subDays } from 'date-fns';
import { useIsMobile } from '@keyslabor/shared/hooks/useBreakPoint';
import { previousWednesday, getDay } from 'date-fns';

interface ItemProps extends BoxStyledProps {
  title: string;
  amount: number;
  borderColor?: string;
}

const calculateToDate = () => {
  const currDay = getDay(new Date());
  if (currDay === 3) {
    return new Date();
  }

  return previousWednesday(new Date());
};

const calculateFromDate = () => {
  return subDays(calculateToDate(), 90);
};

const formatDate = (date: string | Date, forRequest?: boolean) => {
  if (forRequest) {
    return format(new Date(String(date)), 'yyyy-MM-dd').toString();
  }
  return format(new Date(String(date)), 'LLL dd, yyyy').toString();
};

function Item({ amount, title, borderColor, ...restProps }: ItemProps) {
  return (
    <Col
      height="40px"
      width="100%"
      bg="#00000020"
      flexCenter
      justifyContent="space-between"
      px="10px"
      border={borderColor && `2px solid ${borderColor}`}
      borderRadius="4px"
      {...restProps}
    >
      <Text
        fontSize={'16px'}
        fontWeight={'600'}
        css={`
          text-transform: uppercase;
        `}
      >
        {title}
      </Text>
      <Text fontSize={'16px'} fontWeight={'600'}>
        {amount}
      </Text>
    </Col>
  );
}

export default function TurnOver() {
  const theme = useTheme();
  const [data, setData] = useState<TurnOverMeta>();
  const isMobile = useIsMobile();

  const [toDate, setToDate] = useState<Date>(calculateToDate());
  const [fromDate, setFromDate] = useState<Date>(calculateFromDate());

  const [turnOverList] = useAjax({
    type: 'GET',
  });

  const handleList = useCallback(
    async (fromDate, toDate) => {
      const res = await turnOverList({
        url: ajaxRoutes.turnOverList({
          from: formatDate(fromDate, true),
          to: formatDate(toDate, true),
        }),
      });

      setData(res);

      return;
    },
    [turnOverList]
  );

  useEffect(() => {
    // if (!fromDate || !toDate) return;
    handleList(fromDate, toDate);
  }, [fromDate, toDate, handleList]);

  return (
    <PageLayout
      gridAutoFlow="column"
      justifyContent="center"
      gridAutoColumns={isMobile ? '100%' : 'max-content'}
      gap="20px"
      minWidth={'100%'}
    >
      <PageContainer
        padding="20px 0px"
        width={isMobile ? '100%' : '370px'}
        gridTemplateRows="max-content"
        gridAutoColumns="100%"
        minWidth={'100%'}
      >
        <Row height="100%" gridAutoColumns="100%" minWidth={'100%'}>
          <Row
            padding={isMobile ? '0px 20px' : '0px 12px'}
            mt={isMobile && '10px'}
          >
            <Text center fontSize="16px" fontWeight={'600'}>
              Temp to hire turnover results for current dates
            </Text>
            {isMobile && (
              <Divider
                height={'1px'}
                m="30px 0px 20px 0px"
                backgroundColor={'#E5E7F0'}
              />
            )}
          </Row>
          <Col
            gridAutoFlow={isMobile ? 'row' : 'column'}
            gridAutoColumns="1fr 1fr"
            mt={!isMobile && '20px'}
            p={isMobile && '0px 20px'}
          >
            <Row flexCenter gap="10px">
              <Text fontSize="14px" fontWeight="600">
                From
              </Text>
              <CalendarNavigationCompact
                fullWidth={isMobile}
                width={!isMobile && '150px'}
                height={isMobile ? '40px' : '30px'}
                value={fromDate}
                formatedDate={formatDate(fromDate)}
                showIcon={false}
                showYear={false}
                onDateSelect={(date) => {
                  setFromDate(date);
                }}
              />
            </Row>
            <Row flexCenter gap="10px" mt={isMobile && '20px'}>
              <Text fontSize="14px" fontWeight="600">
                To
              </Text>
              <CalendarNavigationCompact
                fullWidth={isMobile}
                width={!isMobile && '150px'}
                height={isMobile ? '40px' : '30px'}
                value={toDate}
                formatedDate={formatDate(toDate)}
                showIcon={false}
                showYear={false}
                onDateSelect={(date) => {
                  setToDate(date);
                }}
              />
            </Row>
          </Col>

          <Row gap="10px" p="0px 20px" alignItems="center">
            <Divider
              height="1px"
              m={isMobile ? '20px 0px 10px 0px' : '30px 0px 20px 0px'}
              backgroundColor={'#E5E7F0'}
            />
            <Item
              amount={data?.people_added}
              bg="rgba(86, 157, 229, 0.80)"
              title={'People added'}
            />
            <Item
              amount={data?.no_show}
              bg="rgba(242, 201, 76, 0.3)"
              title={'Didn’t show up on 1st day '}
            />
            <Item
              amount={data?.terminated}
              bg="rgba(254, 76, 76, 0.5)"
              title={'Separated'}
            />
            <Item
              amount={data?.transferred}
              bg="rgba(33, 150, 83, 0.3)"
              title={'successfully transferred'}
            />
            {isMobile && (
              <Divider
                height="1px"
                m={'10px 0px 10px 0px'}
                backgroundColor={'#E5E7F0'}
              />
            )}
          </Row>
        </Row>
      </PageContainer>
      {!isMobile && (
        <PageContainer gridTemplateRows="100%" width="746px">
          <Flex width="100%" height="100%" flexCenter>
            <Flex width="550px" height="550px" overflow="visible"></Flex>
          </Flex>
        </PageContainer>
      )}
    </PageLayout>
  );
}
